import { CheckCircle2, FileText, Loader2 } from 'lucide-react'

import { OnboardingData } from './types'

interface Step2UploadResumeProps {
  data: OnboardingData
  resumeUploaded: boolean
  resumeUploading: boolean
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  setResumeUploaded: React.Dispatch<React.SetStateAction<boolean>>
  setResumeFilePath: React.Dispatch<React.SetStateAction<string | null>>
  setResumeMdPath: React.Dispatch<React.SetStateAction<string | null>>
  handleInputChangeWrapper: (field: keyof OnboardingData, value: string | number | File | null) => void
}

export function Step2UploadResume({
  data,
  resumeUploaded,
  resumeUploading,
  handleFileChange,
  setResumeUploaded,
  setResumeFilePath,
  setResumeMdPath,
  handleInputChangeWrapper,
}: Step2UploadResumeProps) {
  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="resume" className="block text-sm font-medium text-text-secondary mb-2">
          Upload Your Resume (PDF or Word)
        </label>
        
        {/* File Upload Area */}
        {!resumeUploaded ? (
          <div className="space-y-3">
            <div className="relative">
              <input
                id="resume"
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                disabled={resumeUploading}
                className="block w-full text-sm text-text-muted file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-brand-primary-subtle file:text-brand-primary hover:file:bg-brand-primary-subtle disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            
            {/* Upload Status - Show loading for entire process (upload + conversion) */}
            {resumeUploading && (
              <div className="flex items-center gap-2 px-4 py-3 bg-interactive-subtle border border-border-default rounded-lg">
                <Loader2 className="w-5 h-5 text-interactive animate-spin" />
                <span className="text-sm font-medium text-blue-700">Uploading resume...</span>
              </div>
            )}
            
            <p className="text-sm text-text-muted">
              Optional. You can upload your resume later.
            </p>
          </div>
        ) : (
          /* Success State - Show uploaded file card */
          <div className="mt-2 p-4 bg-brand-primary-subtle border-2 border-brand-primary/20 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <CheckCircle2 className="w-6 h-6 text-brand-primary" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-text-muted flex-shrink-0" />
                  <span className="text-sm font-medium text-text-primary truncate">
                    {data.resumeFile?.name || 'Resume uploaded'}
                  </span>
                </div>
                <p className="text-xs text-brand-primary font-medium">
                  Resume uploaded successfully
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setResumeUploaded(false)
                  setResumeFilePath(null)
                  setResumeMdPath(null)
                  handleInputChangeWrapper('resumeFile', null)
                  // Reset file input
                  const fileInput = document.getElementById('resume') as HTMLInputElement
                  if (fileInput) {
                    fileInput.value = ''
                  }
                }}
                className="flex-shrink-0 text-text-muted hover:text-text-primary transition-colors"
                title="Remove file"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
